import { defineStore } from 'pinia'
import { useStoreAuth } from '@/stores/storeAuth'
import { supabase } from '@/supabase'

/**
 * Home Feed Store
 *
 * A lightweight store for the home page "Reviews in Your Circle" section.
 * Uses the feed API but filtered to only review_create items.
 *
 * Key features:
 * - Default limit of 10 items, can be increased to 15 for scrollable mode
 * - Persists with timestamp validation (max 5 min cache age)
 * - Always fetches fresh on app resume/cold start
 * - Uses the same feed edge function as ViewWebActivity
 */

// Cache validity duration (5 minutes)
const CACHE_MAX_AGE_MS = 5 * 60 * 1000

// Default and max limits
const DEFAULT_LIMIT = 10
const SCROLLABLE_LIMIT = 15

// Debounce delay for realtime events (ms)
const REALTIME_DEBOUNCE_MS = 500

// PostgREST config for direct fetch fallback
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY

/**
 * Query PostgREST directly with an explicit access token.
 * Bypasses the Supabase client's internal auth header injection,
 * which can fail on first load when the client hasn't fully initialised.
 */
async function queryViewWithToken(accessToken, limit) {
  const url = new URL(`${SUPABASE_URL}/rest/v1/feed_items_view`)
  url.searchParams.set('select', '*')
  url.searchParams.set('activity_type', 'eq.review_create')
  url.searchParams.set('order', 'created_at.desc')
  url.searchParams.set('limit', String(limit))

  const res = await fetch(url.toString(), {
    headers: {
      apikey: SUPABASE_ANON_KEY,
      Authorization: `Bearer ${accessToken}`,
      Accept: 'application/json'
    }
  })

  if (!res.ok) {
    const body = await res.text()
    throw new Error(`PostgREST ${res.status}: ${body}`)
  }

  return res.json()
}

export const useStoreHomeFeed = defineStore('storeHomeFeed', {
  persist: {
    pick: ['items', 'lastFetchedAt'],
    serializer: {
      serialize: JSON.stringify,
      deserialize: JSON.parse
    }
  },

  state: () => ({
    // Review items for the home feed
    items: [],

    // Loading state
    loading: false,
    loaded: false,

    // Error state
    error: null,

    // Cache validation
    lastFetchedAt: null,

    // Subscription reference
    subscription: null,

    // Current fetch limit (can be changed for scrollable mode)
    currentLimit: DEFAULT_LIMIT,

    // Debounce timer for realtime events
    _realtimeDebounceTimer: null
  }),

  getters: {
    /**
     * Get review items for display
     */
    reviews: (state) => state.items,

    /**
     * Check if cache is stale (older than CACHE_MAX_AGE_MS)
     */
    isCacheStale: (state) => {
      if (!state.lastFetchedAt) return true
      const age = Date.now() - state.lastFetchedAt
      return age > CACHE_MAX_AGE_MS
    },

    /**
     * Check if we have valid cached data to show immediately
     */
    hasCachedData: (state) => {
      return state.items.length > 0 && state.lastFetchedAt !== null
    }
  },

  actions: {
    /**
     * Initialize the store - fetch fresh data and subscribe to changes
     * @param {Object} options - Optional configuration
     * @param {boolean} options.scrollable - If true, fetch 15 items for scrollable mode
     */
    async init(options = {}) {
      const storeAuth = useStoreAuth()

      if (!storeAuth.user) return

      // Update limit based on scrollable preference
      const newLimit = options.scrollable ? SCROLLABLE_LIMIT : DEFAULT_LIMIT
      const limitChanged = this.currentLimit !== newLimit
      this.currentLimit = newLimit

      // Subscribe to realtime changes
      this.subscribe()

      // If cache is stale, empty, or limit changed, fetch fresh
      if (this.isCacheStale || !this.hasCachedData || limitChanged) {
        await this.fetch()
      } else {
        this.fetchInBackground()
      }
    },

    /**
     * Fetch reviews. Uses the Supabase client first, and if that returns
     * 0 results on a fresh load, falls back to a direct PostgREST call
     * with the explicit access token to bypass any client auth timing issues.
     */
    async fetch() {
      this.loading = true
      this.error = null

      try {
        // Ensure we have a valid session
        const {
          data: { session }
        } = await supabase.auth.getSession()

        if (!session) {
          this.loaded = true
          return
        }

        // Try the normal Supabase client query first
        const { data, error } = await supabase
          .from('feed_items_view')
          .select('*')
          .eq('activity_type', 'review_create')
          .order('created_at', { ascending: false })
          .limit(this.currentLimit)

        if (error) throw error

        // If client query succeeded with data, use it
        if (data && data.length > 0) {
          this.items = data
          this.lastFetchedAt = Date.now()
          this.loaded = true
          return
        }

        // Client returned 0 results — likely the Supabase JS client
        // hasn't attached the JWT to the request yet (INITIAL_SESSION
        // race on first load / HMR). Fall back to a direct PostgREST
        // call with the explicit access token.
        const directData = await queryViewWithToken(
          session.access_token,
          this.currentLimit
        )

        this.items = directData || []
        this.lastFetchedAt = Date.now()
        this.loaded = true
      } catch (err) {
        console.error('[storeHomeFeed] fetch error:', err)
        this.error = err.message || 'Failed to load reviews'
      } finally {
        this.loading = false
      }
    },

    /**
     * Fetch in background without showing loading state
     * Used when we have valid cache but want to refresh
     */
    async fetchInBackground() {
      try {
        const { data, error } = await supabase
          .from('feed_items_view')
          .select('*')
          .eq('activity_type', 'review_create')
          .order('created_at', { ascending: false })
          .limit(this.currentLimit)

        if (error) throw error

        this.items = data || []
        this.lastFetchedAt = Date.now()
        this.loaded = true
      } catch (err) {
        console.error('[storeHomeFeed] Background fetch error:', err)
      }
    },

    /**
     * Force refresh - always fetch fresh data
     */
    async refresh() {
      await this.fetch()
    },

    /**
     * Refresh after app resumes from background
     */
    async refreshAfterResume() {
      await this.fetch()
    },

    /**
     * Subscribe to realtime feed item changes
     */
    subscribe() {
      this.unsubscribe()

      const storeAuth = useStoreAuth()
      const userId = storeAuth.user?.id
      if (!userId) return

      this.subscription = supabase
        .channel('home_feed_items')
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'feed_items',
            filter: `user_id=eq.${userId}`
          },
          (payload) => {
            if (payload.new?.activity_type === 'review_create') {
              // Debounce: following a user can insert many feed items at once,
              // triggering multiple rapid realtime events. Coalesce them into
              // a single background fetch to avoid concurrent state updates
              // that cause Vue "el is null" patch errors.
              clearTimeout(this._realtimeDebounceTimer)
              this._realtimeDebounceTimer = setTimeout(() => {
                this.fetchInBackground()
              }, REALTIME_DEBOUNCE_MS)
            }
          }
        )
        .subscribe()
    },

    /**
     * Refresh only if cache is stale - used when returning to view
     */
    async refreshIfStale() {
      if (this.isCacheStale || !this.hasCachedData) {
        await this.fetch()
      }
    },

    /**
     * Unsubscribe from realtime changes
     */
    unsubscribe() {
      clearTimeout(this._realtimeDebounceTimer)
      if (this.subscription) {
        supabase.removeChannel(this.subscription)
        this.subscription = null
      }
    },

    /**
     * Clear the store
     */
    clear() {
      this.unsubscribe()
      this.items = []
      this.loaded = false
      this.loading = false
      this.error = null
      this.lastFetchedAt = null
    }
  }
})
